import React, { useState, useContext, useCallback } from 'react';
import insertInArrayIf from '@zola-helpers/client/dist/es/util/insertInArrayIf';

type VoidFn = () => void;
type UnsavedChange = { key: string; onUnsavedChanges?: VoidFn };

export type DrawerContextStateType = {
  /** Function to perform when changing internal drawer state, but not closing the drawer entirely; i.e. navigating back to previous screen */
  onBack?: VoidFn;
  /** Function to perform when attempting to close the drawer entirely; */
  onClose?: VoidFn;
  /** Function to perform when attempting to save the actionable interal children of the drawer; Does NOT close the drawer upon completion, but onClose can be included in onSave */
  onSave?: VoidFn;
  /** Object with key and function to catch unsaved changes, fires when trying to exit the drawer (or can be called in react-router when trying to leave route) */
  unsavedChange?: UnsavedChange;
  /** Drawer is visible state */
  isOpen?: boolean;
  /** Show the unsaved changes prompt */
  showSavePrompt?: boolean;
};

const defaultState: DrawerContextStateType = {
  onBack: undefined,
  onClose: undefined,
  onSave: undefined,
  unsavedChange: undefined,
  isOpen: false,
  showSavePrompt: false,
};

export const DrawerContext = React.createContext<
  DrawerContextStateType & {
    /* Callback setter for onBack */
    setOnBack: (onBack?: VoidFn) => void;
    /* Callback setter for onClose */
    setOnClose: (onClose?: VoidFn) => void;
    /* Callback setter for onSave */
    setOnSave: (onSave?: VoidFn) => void;
    /* Callback setter for onUnsavedChanges */
    setOnUnsavedChanges: (unsavedChange: UnsavedChange) => UnsavedChange | undefined;
    /* State setter for isOpen */
    setIsOpen: (isOpen: boolean) => void;
    /* State setter for showSavePrompt */
    setShowSavePrompt: (showSavePrompt: boolean) => void;
  }
>({
  ...defaultState,
  setOnBack: () => undefined,
  setOnClose: () => undefined,
  setOnSave: () => undefined,
  setOnUnsavedChanges: () => undefined,
  setIsOpen: () => undefined,
  setShowSavePrompt: () => undefined,
});

const { Provider } = DrawerContext;

export const DrawerProvider: React.FC<DrawerContextStateType> = ({ children, ...props }) => {
  const [drawerState, setDrawerState] = useState({ ...defaultState, ...props });
  const [, setUnsavedChangeObjs] = useState<UnsavedChange[]>([]);

  const setOnBack = useCallback((onBack?: VoidFn) => {
    setDrawerState(prevState => ({ ...prevState, onBack }));
  }, []);

  const setOnClose = useCallback((onClose?: VoidFn) => {
    setDrawerState(prevState => ({ ...prevState, onClose }));
  }, []);

  const setOnSave = useCallback((onSave?: VoidFn) => {
    setDrawerState(prevState => ({ ...prevState, onSave }));
  }, []);

  /** setOnUnsavedChanges is the only Drawer callback setter that returns a value;
   * To avoid race conditions with react-router, let's make sure we notify the consumer
   * if there is another unsaved change so they can handle appropriately
   */
  const setOnUnsavedChanges = useCallback((unsavedChange: UnsavedChange) => {
    let nextUnsavedChange: UnsavedChange | undefined;
    setUnsavedChangeObjs(prevUnsavedChangeObjs => {
      const existingIdx = prevUnsavedChangeObjs.findIndex(u => u.key === unsavedChange.key);
      if (existingIdx >= 0) {
        // if item already existed in state, replace the function instance
        const newUnsavedCbs = prevUnsavedChangeObjs
          .map((u, i) => {
            if (i === existingIdx) {
              return { ...u, onUnsavedChanges: unsavedChange.onUnsavedChanges };
            }
            return u;
          })
          // then filter any undefined
          .filter(u => u.onUnsavedChanges);
        // Since already filtered, index 0 is the next unsavedChange (but could be the same, if instance was only replaced)
        nextUnsavedChange = newUnsavedCbs[0];
        setDrawerState(prevState => ({ ...prevState, unsavedChange: newUnsavedCbs[0] }));
        return newUnsavedCbs;
      }
      // Otherwise, add to the beginning of array and new unsavedChange bcomes priority
      const newUnsavedCbs = [
        ...insertInArrayIf(!!unsavedChange.key, unsavedChange),
        ...prevUnsavedChangeObjs,
      ];
      nextUnsavedChange = newUnsavedCbs[1];
      setDrawerState(prevState => ({ ...prevState, unsavedChange: newUnsavedCbs[0] }));
      return newUnsavedCbs;
    });
    return nextUnsavedChange;
  }, []);

  const setIsOpen = useCallback((isOpen: boolean) => {
    setDrawerState(prevState => ({ ...prevState, isOpen }));
  }, []);

  const setShowSavePrompt = useCallback((showSavePrompt: boolean) => {
    setDrawerState(prevState => ({ ...prevState, showSavePrompt }));
  }, []);

  return (
    <Provider
      value={{
        ...drawerState,
        setOnBack,
        setOnClose,
        setOnSave,
        setOnUnsavedChanges,
        setIsOpen,
        setShowSavePrompt,
      }}
    >
      {children}
    </Provider>
  );
};

export const useDrawerControls = () => useContext(DrawerContext);
